import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter - counts up to a target number when scrolled into view.
 *
 * Props:
 *  - value: number (target)
 *  - duration: ms (default 1600)
 *  - format: "compact" | "default" | "decimal"
 *  - decimals: number of decimals (for compact mode)
 *  - prefix / suffix: strings around number
 *  - className: tailwind classes
 */
const AnimatedCounter = ({
  value,
  duration = 1600,
  format = "default",
  decimals = 1,
  prefix = "",
  suffix = "",
  className = "",
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const start = () => {
      if (started.current) return;
      started.current = true;
      const startTime = performance.now();
      const tick = (now) => {
        const p = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setCount(eased * value);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, duration]);

  const formatValue = (v) => {
    if (format === "compact") {
      if (v >= 1_000_000) {
        const num = v / 1_000_000;
        return num.toFixed(decimals).replace(/\.0$/, "") + "M";
      }
      if (v >= 1_000) {
        return Math.round(v / 1_000) + "K";
      }
      return Math.round(v).toString();
    }
    if (format === "decimal") {
      return v.toFixed(decimals);
    }
    return Math.round(v).toLocaleString("de-DE");
  };

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatValue(count)}
      {suffix}
    </span>
  );
};

export default AnimatedCounter;
